import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonDatetime,
  IonDatetimeButton,
  IonLabel,
  IonModal,
  isPlatform,
} from "@ionic/react";
import "./ScheduleSync.scss";
import { utilService } from "../../../../services/utilService";
import { CONSTANTS } from "../../../../constants";
import { useToast } from "@agney/ir-toast";
import { RespScheduleModel } from "./Models/ScheduleSync.model";
import { scheduleService } from "./ScheduleSync.service";
import { Plugins } from "@capacitor/core";
import moment from "moment";
import { settingsService } from "../Settings/Settings.service";
import CustomToggle from "../Settings/components/CustomToggle";
import Loader from "../../../../components/Shared/Loader/Loader";
import { useTranslation } from "react-i18next";

interface ScheduleSyncProps {
  onCancel: () => void;
}

const ScheduleSync: React.FC<ScheduleSyncProps> = (props) => {
  const { t } = useTranslation("settings");
  const Toast = useToast();
  const { Browser } = Plugins;

  const googleSignIn = "assets/images/google_sign_in.png";
  const outlookSignIn = "assets/images/outlook_sign_in.png";

  const [fromDate, setFromDate]: any = useState();
  const [toDate, setToDate]: any = useState("");
  const [platformType, setPlatformType]: any = useState();
  const [syncDevice, setSyncDevice]: any = useState();

  const [isGoogleAutoSynced, setIsGoogleAutoSynced] = useState(false);
  const [isOutlookAutoSynced, setIsOutlookAutoSynced] = useState(false);

  const [outlookToggleButton, setOutlookToggleButton] = useState(false);
  const [googleToggleButton, setGoogleToggleButton] = useState(false);

  const [googleAutoSyncDate, setGoogleAutoSyncDate]: any = useState();
  const [outlookAutoSyncDate, setOutlookAutoSyncDate]: any = useState();

  const [isOutlookTokenGenerated, setIsOutlookIsTokenGenrated] =
    useState(false);
  const [isGoogleTokenGenerated, setIsGoogleIsTokenGenrated] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [autoSyncResult, setAutoSyncResult]: any = useState();
  const [openToastModal, setOpenToastModal] = useState(false);

  useEffect(() => {
    loadAutoSyncInitialData();
    getAutoSyncInitialValues();
    setSyncDevice(CONSTANTS.SYNC_DEVICE);

    if (isPlatform("android")) setPlatformType(1);
    else if (isPlatform("ios")) setPlatformType(2);
    else setPlatformType(3);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        getAutoSyncInitialValues();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const getAutoSyncInitialValues = async () => {
    const resp: any = await settingsService.getAutoInitialValues();

    if (resp.isSuccess) {
      const data = resp?.result?.result?.[0];

      setIsOutlookIsTokenGenrated(data?.outlook_calendar?.is_token_generated);
      setIsGoogleIsTokenGenrated(data?.google_calendar?.is_token_generated);

      setOutlookAutoSyncDate(data?.outlook_calendar);
      setGoogleAutoSyncDate(data?.google_calendar);

      setOutlookToggleButton(
        data?.outlook_calendar?.is_auto_synced &&
          data?.outlook_calendar?.is_token_generated
      );

      setGoogleToggleButton(
        data?.google_calendar?.is_auto_synced &&
          data?.google_calendar?.is_token_generated
      );
    }
  };

  const confirmToast = (data: any, e: any) => {
    toggleAutoSync(e, data);

    if (data === CONSTANTS.AUTO_SYNC.GOOGLE) setGoogleToggleButton(e);
    else setOutlookToggleButton(e);

    setOpenToastModal(true);
  };

  const toggleAutoSync = (e: boolean, data: any) => {
    if (data === 4) data = 1;
    else if (data === 5) data = 2;

    setAutoSyncStatus(e, data);
  };

  const setAutoSyncStatus = async (e: any, data: number) => {
    const params = {
      guid: utilService.getLoggedUserGuid(),
      is_auto_synced: e,
      calendar_type: data,
      is_from_settings: true,
      reg_code: utilService.getNetwork(),
    };

    const resp: any = await settingsService.setAutoSyncStatus(params);

    if (resp?.isSuccess) {
      setToastMessage(resp?.message);
      setAutoSyncResult(resp?.result?.[0]);
    } else {
      Toast.error(CONSTANTS.MESSAGES.AUTO_SYNC_ALERT_ERROR);
    }
  };

  const loadAutoSyncInitialData = async () => {
    const resp: any = await scheduleService.getAutoInitialValues();

    setIsGoogleAutoSynced(
      resp?.result?.result?.[0]?.google_calendar?.is_auto_synced
    );
    setIsOutlookAutoSynced(
      resp?.result?.result?.[0]?.outlook_calendar?.is_auto_synced
    );
  };

  const compareDates = (startDate: number | Date, endDate: number | Date) => {
    const start = moment(startDate);
    const end = moment(endDate);

    return end.isSameOrAfter(start);
  };

  const syncAccount = async (calendarType: any) => {
    if (!fromDate || !toDate) {
      Toast.error(t("selectDates"));
      return;
    }

    if (!compareDates(fromDate, toDate)) {
      Toast.error(t("endDateError"));
      return;
    }

    const params = {
      calender_type: calendarType,
      client_type: platformType,
      is_auto_synced:
        calendarType === 1 ? isGoogleAutoSynced : isOutlookAutoSynced,
      start_date: moment(fromDate).format("YYYY-MM-DD"),
      end_date: moment(toDate).format("YYYY-MM-DD"),
      guid: utilService.getLoggedUserGuid(),
      reg_code: utilService.getNetwork(),
    };

    const resp: RespScheduleModel = await scheduleService.submitScheduleSync(
      params
    );

    if (resp.isSuccess && resp.result) {
      if (utilService.isWeb()) window.open(resp.result, "_blank");
      else Browser.open({ url: resp.result });
    } else {
      Toast.error(t("noShifts"));
    }
  };

  return (
    <div className="schedule-sync-container">
      <div className="schedule-sync-back" onClick={props.onCancel}>
        <div className="schedule-sync-back-icon icon-back"></div>
        <div className="schedule-sync-back-header">
          {t("scheduleSync")}
        </div>
      </div>

      <div className="main-container">

        {/* DATE PICKERS */}
        <div className="calendar-picker-container">

          <div className="calendar-wrapper">
            <span className="date-title">{t("startDate")}</span>

            <IonDatetimeButton datetime="startDate" className="date-button" />

            <IonModal keepContentsMounted>
              <IonDatetime
                id="startDate"
                presentation="date"
                locale="en-US"
                onIonChange={(e) => setFromDate(e.detail.value)}
              />
            </IonModal>
          </div>

          <div className="calendar-wrapper">
            <span className="date-title">{t("endDate")}</span>

            <IonDatetimeButton datetime="endDate" className="date-button" />

            <IonModal keepContentsMounted>
              <IonDatetime
                id="endDate"
                presentation="date"
               locale="en-US"
                onIonChange={(e) => setToDate(e.detail.value)}
              />
            </IonModal>
          </div>

        </div>

        {/* SYNC BUTTONS */}

        <div className="sync-container">
          <span className="sync-wrapper">{t("syncWith")}</span>

          <div className="btn-container">

            <div
              className="google-sign-in"
              onClick={() => syncAccount(syncDevice.GOOGLE)}
            >
              <img src={googleSignIn} alt="google sign in" />
            </div>

            <div
              className="outlook-sign-in"
              onClick={() => syncAccount(syncDevice.OUTLOOK)}
            >
              <img src={outlookSignIn} alt="outlook sign in" />
            </div>

          </div>
        </div>

        {/* GOOGLE AUTOSYNC */}

        <div className="google-autosync-container">

          <div className="google-autosync-header">
            <IonLabel>{t("autoSyncGoogle")}</IonLabel>

            {isGoogleTokenGenerated && (
              <p className="auto-sync-text">
                {t("syncedFrom")} {googleAutoSyncDate?.start_date} {t("to")}{" "}
                {googleAutoSyncDate?.end_date}
              </p>
            )}
          </div>

          <CustomToggle
            isSelected={googleToggleButton}
            type={CONSTANTS.AUTO_SYNC.GOOGLE}
            confirmToast={confirmToast}
          />

        </div>

        {/* OUTLOOK AUTOSYNC */}

        <div className="outlook-autosync-container">

          <div className="outlook-autosync-header">
            <IonLabel>{t("autoSyncOutlook")}</IonLabel>

            {isOutlookTokenGenerated && (
              <p className="auto-sync-text">
                {t("syncedFrom")} {outlookAutoSyncDate?.start_date} {t("to")}{" "}
                {outlookAutoSyncDate?.end_date}
              </p>
            )}
          </div>

          <CustomToggle
            isSelected={outlookToggleButton}
            type={CONSTANTS.AUTO_SYNC.OUTLOOK}
            confirmToast={confirmToast}
          />

        </div>

        {/* TOAST MODAL */}

        <IonModal isOpen={openToastModal} backdropDismiss={false}>
          <div className="label-div">
            <div className="label-name">
              {autoSyncResult?.calendar_type === 1
                ? t("signInGoogle")
                : t("signInOutlook")}
            </div>
          </div>

          {toastMessage === "" ? (
            <div className="loader-container">
              <Loader />
            </div>
          ) : (
            <>
              <form className="message-content">{toastMessage}</form>

              <IonButton
                className="submit-btn"
                onClick={() => setOpenToastModal(false)}
              >
                {t("ok")}
              </IonButton>
            </>
          )}
        </IonModal>

      </div>
    </div>
  );
};

export default ScheduleSync;